import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import "./lib/i18n"; // Initialize i18next before render
import { initColorMode } from "./lib/colorMode";
import { initSystemTheme } from "./lib/systemThemes";
import { initAppLifecycle } from "./lib/appLifecycle";
import { initHealthMonitor } from "./lib/healthMonitor";
import { cleanupStaleApks } from "./lib/updateWizard";
import { initPerformanceSettings } from "./lib/performanceSettings";
import { safeSetItem, emergencyCleanup } from '@/lib/safeStorage';

// ── PWA: unregister service workers in preview/iframe contexts ──
(() => {
  const isInIframe = (() => { try { return window.self !== window.top; } catch { return true; } })();
  const isPreviewHost =
    window.location.hostname.includes('id-preview--') ||
    window.location.hostname.includes('lovableproject.com');
  if (isPreviewHost || isInIframe) {
    navigator.serviceWorker?.getRegistrations().then(regs => regs.forEach(r => r.unregister()));
  } else if (navigator.serviceWorker) {
    // Force-reload when a new SW takes control so stale chunks are never served
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });
  }
})();

// ── Early init (before render) ──────────────────────────────
// Apply color mode immediately to prevent flash of wrong theme
initColorMode();
initSystemTheme();
initPerformanceSettings();

// Start adaptive theme scheduler (auto light/dark based on time of day)
import('./lib/themeScheduler').then(({ startThemeScheduler }) => startThemeScheduler());

// Initialize offline/online auto-detection
import('./lib/offlineSync').then(({ initOfflineDetection }) => initOfflineDetection());

// Pet pause flag now persists across restarts (user controls via profile page)

// ── Android navigation bar safe area detection ─────────────
// Android 3-button nav doesn't report env(safe-area-inset-bottom).
// We detect the gap between window.innerHeight and visualViewport and
// expose --android-nav-offset as a CSS variable on :root.
(function detectAndroidNavBar() {
  const isAndroid = /Android/i.test(navigator.userAgent);
  if (!isAndroid) return;

  const root = document.documentElement;

  function hasSafeAreaInset(side: 'bottom' | 'left' | 'right'): boolean {
    const el = document.createElement('div');
    el.style.cssText = `position:fixed;${side}:0;left:0;width:0;padding-${side}:env(safe-area-inset-${side},0px);pointer-events:none;visibility:hidden`;
    root.appendChild(el);
    const val = side === 'bottom' ? el.offsetHeight : el.offsetWidth;
    el.remove();
    return val > 0;
  }

  /** Compute nav bar offset using multiple detection methods; return max */
  function detectNavBar(axis: 'bottom' | 'side'): number {
    const screenW = window.screen.availWidth || window.screen.width;
    const screenH = window.screen.availHeight || window.screen.height;
    const innerW = window.innerWidth;
    const innerH = window.innerHeight;
    const candidates: number[] = [];

    if (axis === 'bottom') {
      // Method 1: screen.availHeight - innerHeight (classic, works in browser)
      candidates.push(screenH - innerH);
      // Method 2: outerHeight - innerHeight (works in some WebViews)
      if (window.outerHeight > 0) candidates.push(window.outerHeight - innerH);
      // Method 3: innerHeight - visualViewport.height (reliable in Capacitor WebViews)
      if (window.visualViewport) candidates.push(innerH - window.visualViewport.height);
    } else {
      candidates.push(screenW - innerW);
      if (window.outerWidth > 0) candidates.push(window.outerWidth - innerW);
      if (window.visualViewport) candidates.push(innerW - window.visualViewport.width);
    }

    // Take the max valid candidate (must be positive, reasonable range)
    let best = 0;
    for (const c of candidates) {
      if (c > 8 && c < 120 && c > best) best = c;
    }
    return best;
  }

  function update() {
    const isLandscape = window.innerWidth > window.innerHeight;

    if (!isLandscape) {
      // ── Portrait: nav bar at bottom ──
      root.style.setProperty('--android-nav-left', '0px');
      root.style.setProperty('--android-nav-right', '0px');

      if (hasSafeAreaInset('bottom')) {
        root.style.setProperty('--android-nav-offset', '0px');
        return;
      }
      const navHeight = detectNavBar('bottom');
      root.style.setProperty('--android-nav-offset', `${navHeight}px`);
    } else {
      // ── Landscape: nav bar on a side (usually right) ──
      root.style.setProperty('--android-nav-offset', '0px');

      if (hasSafeAreaInset('left') || hasSafeAreaInset('right')) {
        root.style.setProperty('--android-nav-left', '0px');
        root.style.setProperty('--android-nav-right', '0px');
        return;
      }
      const sideNav = detectNavBar('side');
      root.style.setProperty('--android-nav-right', `${sideNav}px`);
      root.style.setProperty('--android-nav-left', '0px');
    }
  }

  update();
  window.addEventListener('resize', update);
  window.addEventListener('orientationchange', update);
  window.visualViewport?.addEventListener('resize', update);

  // Capacitor WebViews sometimes report correct values only after a delay
  // Re-check periodically for the first 10 seconds
  let recheckCount = 0;
  const recheckInterval = setInterval(() => {
    recheckCount++;
    update();
    if (recheckCount >= 5) clearInterval(recheckInterval);
  }, 2000);
})();

// ── Privacy protections (moved from inline HTML scripts) ────
(function initPrivacyProtections() {
  // Prevent print-screen from capturing content
  document.addEventListener('keyup', (e) => {
    if (e.key === 'PrintScreen' && document.hasFocus()) {
      navigator.clipboard.writeText('').catch(() => {});
    }
  });

  // Clear clipboard on page unload for security
  window.addEventListener('beforeunload', () => {
    try {
      if (document.hasFocus()) {
        navigator.clipboard.writeText('');
      }
    } catch {}
  });
})();

// Start lifecycle manager (handles background/resume reconnection)
initAppLifecycle();

// Remove any leftover APK files from cache (fire-and-forget)
cleanupStaleApks();

// ── Global crash logger (item 11) — ring buffer of last 20 errors ──
// Also handles QuotaExceededError recovery to prevent cascading crashes
(function initCrashLogger() {
  const MAX_ENTRIES = 20;
  const LOG_KEY = 'aphenis_crash_log';

  function logCrash(source: string, msg: string) {
    try {
      const raw = localStorage.getItem(LOG_KEY);
      const log: { ts: number; src: string; msg: string }[] = raw ? JSON.parse(raw) : [];
      log.push({ ts: Date.now(), src: source, msg: msg.slice(0, 300) });
      while (log.length > MAX_ENTRIES) log.shift();
      safeSetItem(LOG_KEY, JSON.stringify(log));
    } catch {}
  }

  function isQuotaError(err: unknown): boolean {
    if (err instanceof DOMException && (err.name === 'QuotaExceededError' || err.code === 22)) return true;
    if (err instanceof Error && err.message?.includes('QuotaExceededError')) return true;
    return false;
  }

  function handleQuotaRecovery() {
    try {
      emergencyCleanup();
    } catch {
      // Inline fallback cleanup
      const expendable = ['aphenis_crash_log', 'aphenis_xp_log', 'aphenis_game_history', 'aphenis_voice_transcriptions', 'aphenis_pet_activity_feed'];
      for (const key of expendable) { try { localStorage.removeItem(key); } catch {} }
    }
  }

  window.onerror = (_msg, _src, _line, _col, err) => {
    if (isQuotaError(err)) handleQuotaRecovery();
    logCrash('onerror', err?.message || String(_msg));
  };
  window.addEventListener('unhandledrejection', (e) => {
    if (isQuotaError(e.reason)) handleQuotaRecovery();
    logCrash('unhandledrejection', e.reason?.message || String(e.reason));
  });
})();

createRoot(document.getElementById("root")!).render(<App />);

// Start proactive health monitor after render
initHealthMonitor();
